import {
  TextField,
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  TablePagination,
  Typography,
  Card,
  InputAdornment,
} from '@mui/material'
import React from 'react'
import { IIngredient } from 'interfaces/IIngredient'
import makeStyles from '@mui/styles/makeStyles'
import API from '@aws-amplify/api'
import SearchIcon from '@mui/icons-material/Search'
import { clampDecimalPlaces } from 'utils/clampDecimalPlaces'

const useStyles = makeStyles((theme) => ({
  tinyCell: { width: '1%' },
  smallCell: { width: '5%' },
  mediumCell: { width: '10%' },
  tableSubRow: { backgroundColor: '#f7fbff' },
  selectedCard: { backgroundColor: 'rgba(255, 159, 165, 0.08)' },
}))

const ROWS_PER_PAGE = 25

export default function IngredientFinder(props: {
  selectedIngredients?: Partial<IIngredient>[]
  getSelectedIngredients?: (ingredients: Partial<IIngredient>[]) => void
  ingredientData?: Partial<IIngredient>[]
  editableQty?: boolean
  showQty?: boolean
  showInventory?: boolean
  showSelectedIngredients?: boolean
  hideSearch?: boolean
  onValidationChange?: (hasErrors: boolean) => void
  validateStock?: boolean
}) {
  const [ingredients, setIngredients] = React.useState<Partial<IIngredient>[]>(
    props.ingredientData || []
  )
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isLoading, setIsLoading] = React.useState<any>(
    props.ingredientData ? false : true
  )
  const [selectedIngredients, setSelectedIngredients] = React.useState<
    Partial<IIngredient>[]
  >(props.selectedIngredients || [])
  const [page, setPage] = React.useState(0)
  const classes = useStyles()

  React.useEffect(() => {
    if (!props.ingredientData || !props.ingredientData.length) loadIngredients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  React.useEffect(() => {
    if (props.getSelectedIngredients)
      props.getSelectedIngredients(selectedIngredients)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIngredients])

  React.useEffect(() => {
    if (props.onValidationChange && props.showInventory) {
      const validateStock = props.validateStock !== false
      const hasErrors =
        validateStock &&
        ingredients.some(
          (ingredient) =>
            ingredient.qty !== null &&
            ingredient.qty !== undefined &&
            Number(ingredient.qty) > Number(ingredient.stock || 0)
        )
      props.onValidationChange(!!hasErrors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredients, props.validateStock])

  const loadIngredients = (): void => {
    setIsLoading(true)
    API.get('products', `/ingredients/getIngredient?pageSize=99999`, {})
      .then((response: IIngredient[]) => {
        setIngredients(response)
      })
      .catch((error) => {
        // Error fetching ingredients - silently fail
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const getFilteredIngredients = () => {
    return ingredients.filter((ingredient) => {
      const nameMatch = (ingredient.ingredientName || '')
        .toUpperCase()
        .includes(searchTerm.toUpperCase())
      const idMatch = (ingredient.ingredientId || '')
        .toUpperCase()
        .includes(searchTerm.toUpperCase())
      return nameMatch || idMatch
    })
  }

  const handleQtyChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ingredientId: string
  ) => {
    // Parse value - allow empty string for deletion, allow decimals
    const rawValue = clampDecimalPlaces(e.target.value)
    let qty: number | null = rawValue === '' ? null : Number(rawValue)

    if (qty !== null) {
      if (Number.isNaN(qty)) qty = null
    }

    if (qty !== null && qty < 0) {
      qty = 0
    }

    // Update ingredients with RAW string to allow "0." or "0.0" typing
    setIngredients((prevState) =>
      prevState.map((prevIngredient) =>
        prevIngredient.ingredientId === ingredientId
          ? { ...prevIngredient, qty: rawValue as any }
          : prevIngredient
      )
    )

    const isSelected = isIngredientSelected(ingredientId)
    // Use numeric qty for selection logic and parent state
    if (!qty) {
      setSelectedIngredients((prevState) => {
        return prevState.filter(
          (ingredient) => ingredient.ingredientId !== ingredientId
        )
      })
    } else {
      setSelectedIngredients((prevState) => {
        const notSelectedAndNotEmpty = Number(qty) > 0 && !isSelected
        if (notSelectedAndNotEmpty) {
          const matchingIngredient =
            ingredients.find(
              (ingredient) => ingredient.ingredientId === ingredientId
            ) || {}
          return [...prevState, { ...matchingIngredient, qty }]
        } else {
          return selectedIngredients.map((prevIngredient) =>
            prevIngredient.ingredientId === ingredientId
              ? { ...prevIngredient, qty }
              : prevIngredient
          )
        }
      })
    }
  }

  const isIngredientSelected = (ingredientId: string) => {
    return !!selectedIngredients.find(
      (selectedIngredient) => selectedIngredient.ingredientId === ingredientId
    )
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setPage(0)
  }

  const getIngredientsToShow = () => {
    let data = getFilteredIngredients().slice(
      page * ROWS_PER_PAGE,
      page * ROWS_PER_PAGE + ROWS_PER_PAGE
    )
    return data
  }

  return (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>
        Cari Bahan Baku
      </Typography>
      {!props.hideSearch && (
        <TextField
          variant="outlined"
          fullWidth
          placeholder="Cari Kode/Nama Bahan Baku..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {props.showQty && <TableCell>Qty</TableCell>}
              {props.showInventory && <TableCell>Stok</TableCell>}
              <TableCell>Nama</TableCell>
              <TableCell>Kode</TableCell>
              <TableCell>Satuan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (
              getIngredientsToShow().map((ingredient, idx) => (
                <TableRow
                  key={ingredient.ingredientId || idx}
                  className={
                    isIngredientSelected(ingredient.ingredientId || '')
                      ? classes.selectedCard
                      : ''
                  }
                >
                  {props.showQty && (
                    <TableCell>
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        value={ingredient.qty || ''}
                        onChange={(e) =>
                          handleQtyChange(e, ingredient.ingredientId || '')
                        }
                        inputProps={{
                          min: 0,
                          step: '0.000000000001',
                        }}
                        disabled={!props.editableQty}
                        error={
                          props.showInventory &&
                          props.validateStock !== false &&
                          ingredient.qty !== null &&
                          ingredient.qty !== undefined &&
                          Number(ingredient.qty) > Number(ingredient.stock || 0)
                        }
                        helperText={
                          props.showInventory &&
                          props.validateStock !== false &&
                          ingredient.qty !== null &&
                          ingredient.qty !== undefined &&
                          Number(ingredient.qty) > Number(ingredient.stock || 0)
                            ? `Melebihi stok (${ingredient.stock})`
                            : ''
                        }
                      />
                    </TableCell>
                  )}
                  {props.showInventory && (
                    <TableCell>{ingredient.stock}</TableCell>
                  )}
                  <TableCell>{ingredient.ingredientName}</TableCell>
                  <TableCell>{ingredient.ingredientId}</TableCell>
                  <TableCell>KG</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container justifyContent="flex-end">
        <TablePagination
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          component="div"
          count={getFilteredIngredients().length}
          rowsPerPage={ROWS_PER_PAGE}
          page={page}
          onPageChange={handleChangePage}
        />
      </Grid>
      {props.showSelectedIngredients && selectedIngredients.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1">Bahan Baku Terpilih:</Typography>
          {selectedIngredients.map((ingredient, idx) => (
            <Card
              key={ingredient.ingredientId || idx}
              className={classes.selectedCard}
            >
              <Box p={1}>
                {ingredient.ingredientName} ({ingredient.qty} kg)
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  )
}
